import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { IAuthResponse } from '@g99-product-web-ui/shared-interface-lib';
import {
  ANALYTICS_EVENTS,
  API_ENDPOINTS,
  LOCAL_STORAGE_KEYS,
} from '@g99-product-web-ui/shared-util-lib';
import { EMPTY, Subscription, fromEvent, merge, of, timer } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { ServiceType } from '../../constants/service-types.enum';
import { HttpService } from '../../core/http-service/http.service';

/**
 * Service to proxy analytics events to the backend.
 * This prevents exposing Amplitude keys in the frontend.
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsProxyService implements OnDestroy {
  private _heartbeatSubscription: Subscription | null = null;
  private _pageTrackingSubscription: Subscription | null = null;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly HEARTBEAT_INTERVAL = 1 * 60 * 1000; // 1 minute
  private _lastHeartbeatTime = 0;

  constructor(
    private readonly _httpService: HttpService,
    private readonly _router: Router
  ) {}

  ngOnDestroy(): void {
    this.stopHeartbeat();
    this.stopPageTracking();
  }

  /**
   * Initialize automated page tracking.
   * Tracks a 'Page Viewed' event on every successful route change.
   */
  public initPageTracking(): void {
    if (this._pageTrackingSubscription) {
      return;
    }

    this._pageTrackingSubscription = this._router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        const url = event.urlAfterRedirects || event.url;
        this.track(ANALYTICS_EVENTS.NAVIGATION.PAGE_VIEWED, {
          url: url,
          page_name: this._getPageNameFromUrl(url),
        });
      });
  }

  /**
   * Stops automated page tracking.
   */
  public stopPageTracking(): void {
    if (this._pageTrackingSubscription) {
      this._pageTrackingSubscription.unsubscribe();
      this._pageTrackingSubscription = null;
    }
  }

  /**
   * Tracks an event by sending it to the backend proxy.
   *
   * @param eventName Name of the event (e.g., 'User Login Success')
   * @param properties Optional metadata properties
   * @param description Optional description
   */
  public track(
    eventName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    properties: any = {},
    description = ''
  ): void {
    const url = API_ENDPOINTS.PUBLIC_V2_ENDPOINTS.analyticsTrack;
    let enrichedProperties = { ...properties };

    // Automatically append user context if available
    const userContext = this._getUserFromStorage();

    // 🛑 Exclude internal/support users from tracking
    if (userContext) {
      const isSupport =
        userContext.supportUser === true || userContext.supportUser === 'true';

      // Use includes() for robust role checking (handles arrays or comma-separated strings)
      const rolesValue = Array.isArray(userContext.roles)
        ? userContext.roles.join(',')
        : String(userContext.roles || '');

      const isAdminOrSupportRole =
        rolesValue.includes('G_SUPER_ADMIN') ||
        rolesValue.includes('G_SUPPORT_USER');

      if (isSupport || isAdminOrSupportRole) {
        return; // Silently skip
      }

      if (!enrichedProperties['User ID']) {
        enrichedProperties = {
          ...enrichedProperties,
          'User ID': userContext.username || userContext.email,
          dbUserId: userContext.id,
          'Business ID': userContext.businessId,
          email: userContext.username || userContext.email,
        };
      }
    }

    const payload = {
      eventName,
      properties: enrichedProperties,
      description,
    };

    // We use ServiceType.PUBLIC to skip standard auth filters if necessary,
    // although our backend endpoint is specifically permitted in SecurityConfig.
    this._httpService
      .post(url, payload, { attachTenantId: true }, ServiceType.PUBLIC)
      .subscribe({
        error: (err) => console.error('Analytics track failed', err),
      });
  }

  /**
   * Initialize the heartbeat tracker.
   * Sends a heartbeat every 1 minute if the tab is visible.
   */
  public initHeartbeat(): void {
    if (this._heartbeatSubscription) {
      return;
    }

    const visibility$ = merge(
      of(document.visibilityState),
      fromEvent(document, 'visibilitychange').pipe(
        map(() => document.visibilityState)
      )
    );

    this._heartbeatSubscription = visibility$
      .pipe(
        switchMap((state) => {
          if (state === 'visible') {
            return timer(0, this.HEARTBEAT_INTERVAL);
          }
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.sendHeartbeat();
      });
  }

  /**
   * Stops the heartbeat tracker.
   */
  public stopHeartbeat(): void {
    if (this._heartbeatSubscription) {
      this._heartbeatSubscription.unsubscribe();
      this._heartbeatSubscription = null;
    }
  }

  /**
   * Sends a heartbeat event to indicate the user is still active.
   * Includes a 30-second throttle to prevent API spam from rapid tab switching.
   */
  public sendHeartbeat(): void {
    const now = Date.now();
    // 🛑 Prevent firing if we already fired within the last 30 seconds
    if (now - this._lastHeartbeatTime < 30000) {
      return;
    }
    this._lastHeartbeatTime = now;

    this.track(ANALYTICS_EVENTS.SESSIONS.USER_HEARTBEAT, {
      url: window.location.href,
      timestamp: now,
    });
  }

  /**
   * Extracts a readable page name from the URL.
   */
  private _getPageNameFromUrl(url: string): string {
    const segments = url.split('/').filter((s) => s && !s.match(/^\d+$/));
    if (segments.length === 0) {
      return 'Dashboard';
    }

    // Map specific paths to better names if needed
    const primarySegment = segments[0];
    const name =
      primarySegment.charAt(0).toUpperCase() + primarySegment.slice(1);

    return name.replace(/-/g, ' ');
  }

  /**
   * Helper to get user info directly from localStorage to avoid circular dependencies.
   */
  private _getUserFromStorage(): IAuthResponse | null {
    try {
      const user = localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_USER);
      return user ? JSON.parse(user) : null;
    } catch (e) {
      return null;
    }
  }
}
